import React, { useCallback } from 'react';
import './draggable-entity-card.css';
import parse from 'style-to-object';
import './unreachable-marker.css';
import {
	getBlockedBackground,
	getCanvasEntityDOMId,
} from '../../services/editor/ui';
import { Canvas, DraggableObject } from '../../lib/canvas';
import {
	CanvasEntityDisabledReason,
	EntityInstanceErrorCode,
	EntityState,
} from '@elyx-code/project-logic-tree';
import { EditorService } from '../../services/editor';
import CanvasPopupBaseComponent from '../../lib/popup/CanvasPopupBaseComponent';
import { useCanvasPopup } from '../../services/editor/CanvasPopup';

interface IUnreachableMarkerProps {
	entity: EntityState;
	parentDisabledReasons: CanvasEntityDisabledReason[];
	project: EditorService;
	draggableObject: DraggableObject;
}

/*
	Counterpart to VariableScopeMarker, but anchored to the RIGHT side of the
	card header and gated on the entity having a "blocked reachability" error.
	Renders nothing when the entity is reachable — same conditional-render
	pattern as VariableScopeMarker for `local-instance`.

	The "blocked reachability" condition reuses the same `errors.find` check
	that was previously drafted (and commented out) inside
	`logic-error/LogicErrorIconMenu.tsx` — keeping the trigger in lockstep so
	the icon-menu badge and this marker stay visually consistent.
*/
export default function UnreachableMarker(props: IUnreachableMarkerProps) {
	const hasBlockedReachabilityError = !!props.entity.errors.find(
		(e) =>
			e.code === EntityInstanceErrorCode.LastCalledEntityMustBeReturnEntity,
	);

	// Tooltip body. The message is static (the user asked for a simple,
	// non-conditional explanation) so the memo's deps list is empty — the
	// callback never needs to recreate. Same `useCallback` + `useCanvasPopup`
	// pattern that VariableScopeMarker uses, so the popup is positioned and
	// torn down by the canvas-popup service rather than living in the DOM
	// flow next to the marker (which would clip against the card's edges).
	const memoizedCanvasPopupBody = useCallback(
		() => (
			<CanvasPopupBaseComponent
				containerClasses={['tooltip', 'tooltip--large-font']}
			>
				This node must connect to a termination node.
				<br />
				<br />
				Otherwise the termination node will end the execution before it can
				reach this node
			</CanvasPopupBaseComponent>
		),
		[],
	);

	// `useCanvasPopup` is a hook — must run on every render regardless of
	// whether the marker ends up visible, otherwise React's hook-order check
	// will fail. The early `return null` below comes after the hook call for
	// the same reason. Mirrors how VariableScopeMarker always calls the hook
	// even when it ultimately returns null for `local-instance` scope.
	const canvasPopup = useCanvasPopup(
		{
			editor: props.project,
			id: 'canvas--unreachable-marker--' + props.entity.id,
			arrowClasses: ['tooltip-arrow'],
			anchorQuerySelector:
				'#' + getCanvasEntityDOMId(props.entity) + '--unreachable-marker',
			placement: 'top',
			parentCanvasObject: props.draggableObject,
		},
		memoizedCanvasPopupBody,
	)
		.setupHover({
			closeDelay: 80,
			openDelay: 600,
		})
		.onBeforeShow(() => hasBlockedReachabilityError);

	if (!hasBlockedReachabilityError) {
		return null;
	}

	return (
		<span
			ref={canvasPopup.anchorRef}
			className={[
				// 'error-background',
				'unreachable-marker',
				...(!!props.parentDisabledReasons.length ? ['disabled'] : []),
			].join(' ')}
			id={`${getCanvasEntityDOMId(props.entity)}--unreachable-marker`}
			// style={parse(
			// 	getBlockedBackground(props.entity.type, {
			// 		invertedStripes: true,
			// 	}),
			// )}
			{...{
				[Canvas.OBJ_ATTR_NAME]: `${getCanvasEntityDOMId(
					props.entity,
				)}--unreachable-marker`,
			}}
		>
			Unreachable!
		</span>
	);
}
